import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../api';

/* ─────────────────────────────────────────────────────────────────────────────
   UserDashboard.js  v3
   Design overhaul:
   - Matches Profile.js / UserInfo.jsx visual language (Inter/DM Sans, light
     surface, cyan accent, same card radius + border tokens)
   - Avatar panel replaced with clean profile card (photo + stats + plan badge)
   - Vehicle tag interactions panel — shows every vehicle NFC scan, reason,
     finder contacts cross-referenced from the accessory analytics
   - Lost Tag Alerts tab (polls /api/flapfindmy/my-alerts every 30s)
   - Analytics tab with line chart + donut
   - Admin route shortcut for superadmin/admin
───────────────────────────────────────────────────────────────────────────── */

const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');`;

/* ── Design tokens — same palette as Profile.js + UserInfo.jsx ─────────────── */
const T = {
  bg:      '#f0f3ff',     // matches UserInfo light mode
  bgDark:  '#060b14',
  surface: '#ffffff',
  surfD:   'rgba(13,22,40,0.85)',
  border:  'rgba(0,0,0,0.08)',
  borderD: 'rgba(56,189,248,0.12)',
  text:    '#111827',
  textD:   '#e8f4fd',
  muted:   '#6b7280',
  mutedD:  '#4a7a9b',
  dim:     '#e5e7eb',
  dimD:    '#1a3050',
  cyan:    '#38bdf8',
  teal:    '#2dd4bf',
  green:   '#34d399',
  amber:   '#fbbf24',
  red:     '#f87171',
  purple:  '#818cf8',
  orange:  '#fb923c',
};

/* ── Responsive hook ────────────────────────────────────────────────────────── */
function useBreakpoint() {
  const [bp, setBp] = useState(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
    return w < 640 ? 'mobile' : w < 1024 ? 'tablet' : 'desktop';
  });
  useEffect(() => {
    const fn = () => setBp(window.innerWidth < 640 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop');
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return bp;
}

function decodeToken() {
  try { return JSON.parse(atob((localStorage.getItem('token')||'').split('.')[1]||'e30=')); }
  catch { return {}; }
}

function rgb(hex) {
  const c = (hex||'#38bdf8').replace('#','');
  const f = c.length===3?c.split('').map(x=>x+x).join(''):c;
  const n = parseInt(f,16);
  return `${(n>>16)&255},${(n>>8)&255},${n&255}`;
}

const timeAgo = d => {
  if (!d) return '—';
  const s = Math.floor((Date.now()-new Date(d))/1000);
  if (s<60) return 'just now';
  if (s<3600) return `${Math.floor(s/60)}m ago`;
  if (s<86400) return `${Math.floor(s/3600)}h ago`;
  return new Date(d).toLocaleDateString();
};

/* ── Animated counter ───────────────────────────────────────────────────────── */
function AnimNum({ value, duration=1000 }) {
  const [n, setN] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    const start = Date.now(), target = Number(value)||0;
    const tick = () => {
      const p = Math.min((Date.now()-start)/duration, 1);
      setN(Math.round(target*(1-Math.pow(1-p,3))));
      if (p<1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [value, duration]);
  return <span>{n.toLocaleString()}</span>;
}

/* ── Donut chart ─────────────────────────────────────────────────────────────── */
function Donut({ segments, size=100, thick=14, label, sub }) {
  const r = (size-thick)/2, circ = 2*Math.PI*r, cx=size/2, cy=size/2;
  const total = segments.reduce((a,s)=>a+s.value,0)||1;
  let off=0;
  return (
    <div style={{position:'relative',width:size,height:size,flexShrink:0}}>
      <svg width={size} height={size} style={{transform:'rotate(-90deg)'}}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth={thick}/>
        {segments.filter(s=>s.value>0).map((s,i)=>{
          const d=s.value/total*circ;
          const el=<circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={s.color} strokeWidth={thick}
            strokeDasharray={`${d} ${circ}`} strokeDashoffset={-off*circ}
            style={{transition:'stroke-dasharray .8s ease'}}/>;
          off+=s.value/total; return el;
        })}
      </svg>
      {label&&<div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',
        alignItems:'center',justifyContent:'center'}}>
        <div style={{fontSize:size*.2,fontWeight:800,color:T.text,fontFamily:'Syne,sans-serif',lineHeight:1}}>{label}</div>
        {sub&&<div style={{fontSize:size*.1,color:T.muted,marginTop:1}}>{sub}</div>}
      </div>}
    </div>
  );
}

/* ── Spark bar ───────────────────────────────────────────────────────────────── */
function SparkBars({ data=[], color=T.cyan, h=40 }) {
  const max = Math.max(...data.map(d=>d.value||0),1);
  return (
    <div style={{display:'flex',alignItems:'flex-end',gap:2,height:h}}>
      {data.map((d,i)=>{
        const v=d.value||0, barH=Math.max(2,(v/max)*(h-2));
        return <div key={i} title={`${d.label||''}: ${v}`}
          style={{flex:1,borderRadius:'2px 2px 0 0',minWidth:2,height:barH,
            background:color,opacity:.3+.7*(v/max),transition:'height .6s ease',transitionDelay:`${i*15}ms`}}/>;
      })}
    </div>
  );
}

/* ── Line chart ──────────────────────────────────────────────────────────────── */
function LineChart({ data=[], color=T.cyan, height=72 }) {
  if (data.length<2) return <div style={{color:T.muted,fontSize:12,paddingTop:height/3,textAlign:'center'}}>No data yet</div>;
  const vals = data.map(d=>d.value||0);
  const max=Math.max(...vals,1), min=Math.min(...vals);
  const W=300, H=height;
  const pts = vals.map((v,i)=>`${(i/(vals.length-1))*W},${H-((v-min)/(max-min||1))*(H-6)-3}`);
  const area = `0,${H} ${pts.join(' ')} ${W},${H}`;
  const gid = color.replace('#','g');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',height,overflow:'visible'}}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity=".25"/>
          <stop offset="100%" stopColor={color} stopOpacity=".02"/>
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#${gid})`}/>
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="1.5"/>
      {pts.map((p,i)=>{
        const [x,y]=p.split(',');
        return <circle key={i} cx={x} cy={y} r="2.5" fill={color}/>;
      })}
    </svg>
  );
}

/* ── Activity ring ───────────────────────────────────────────────────────────── */
function Ring({ pct, color, size=60, label }) {
  const r=22, circ=2*Math.PI*r;
  return (
    <div style={{position:'relative',width:size,height:size}}>
      <svg width={size} height={size} style={{transform:'rotate(-90deg)'}}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth={5}/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={5}
          strokeLinecap="round" strokeDasharray={`${Math.min(pct,1)*circ} ${circ}`}
          style={{transition:'stroke-dasharray 1s ease'}}/>
      </svg>
      {label&&<div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',
        justifyContent:'center',fontSize:9,fontWeight:700,color,fontFamily:'JetBrains Mono,monospace'}}>{label}</div>}
    </div>
  );
}

/* ── Stat card ───────────────────────────────────────────────────────────────── */
function StatCard({ icon, label, value, sub, color=T.cyan, anim=true, delay=0 }) {
  const [vis, setVis] = useState(false);
  useEffect(()=>{ const t=setTimeout(()=>setVis(true),delay); return()=>clearTimeout(t); },[delay]);
  const r = rgb(color);
  return (
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,padding:'16px 18px',
      position:'relative',overflow:'hidden',
      opacity:vis?1:0,transform:vis?'translateY(0)':'translateY(12px)',
      transition:'opacity .4s ease,transform .4s ease',
      boxShadow:'0 1px 6px rgba(0,0,0,0.06)'}}>
      <div style={{position:'absolute',inset:0,background:`radial-gradient(ellipse at top right,rgba(${r},.07),transparent 65%)`}}/>
      <div style={{position:'absolute',top:12,right:14,fontSize:20,opacity:.15}}>{icon}</div>
      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:'uppercase',
        letterSpacing:1.5,marginBottom:6,fontFamily:'JetBrains Mono,monospace'}}>{label}</div>
      <div style={{fontSize:24,fontWeight:800,color,fontFamily:'Syne,sans-serif',lineHeight:1,letterSpacing:-.5}}>
        {anim?<AnimNum value={value}/>:value}
      </div>
      {sub&&<div style={{fontSize:11,color:T.muted,marginTop:5}}>{sub}</div>}
    </div>
  );
}

/* ── Quick action pill ───────────────────────────────────────────────────────── */
function QAction({ icon, label, color, onClick, badge }) {
  const [h, setH] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)}
      style={{position:'relative',display:'flex',flexDirection:'column',alignItems:'center',gap:5,
        padding:'12px 10px',borderRadius:12,cursor:'pointer',transition:'all .15s',fontFamily:'inherit',
        background:h?`rgba(${rgb(color)},.08)`:T.surface,
        border:`1px solid ${h?color:T.border}`,
        transform:h?'translateY(-1px)':'none',
        boxShadow:h?`0 4px 12px rgba(${rgb(color)},.15)`:'0 1px 4px rgba(0,0,0,0.05)',
        minWidth:68}}>
      <span style={{fontSize:20}}>{icon}</span>
      <span style={{fontSize:9,color:h?color:T.muted,fontWeight:700,textTransform:'uppercase',
        letterSpacing:.8,fontFamily:'JetBrains Mono,monospace',whiteSpace:'nowrap'}}>{label}</span>
      {badge>0&&<div style={{position:'absolute',top:6,right:6,width:16,height:16,borderRadius:'50%',
        background:T.red,display:'flex',alignItems:'center',justifyContent:'center',
        fontSize:9,fontWeight:800,color:'#fff'}}>{badge>9?'9+':badge}</div>}
    </button>
  );
}

/* ── Section header ──────────────────────────────────────────────────────────── */
function SecHead({ title, icon, action, actionLabel }) {
  return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
      <div style={{display:'flex',alignItems:'center',gap:7}}>
        <span style={{fontSize:14}}>{icon}</span>
        <span style={{fontSize:12,fontWeight:700,color:T.muted,textTransform:'uppercase',
          letterSpacing:1.4,fontFamily:'JetBrains Mono,monospace'}}>{title}</span>
      </div>
      {action&&<button onClick={action}
        style={{fontSize:11,color:T.cyan,background:'none',border:'none',cursor:'pointer',
          fontWeight:600,fontFamily:'inherit'}}>{actionLabel||'View all →'}</button>}
    </div>
  );
}

/* ── Card wrapper ────────────────────────────────────────────────────────────── */
const Card = ({children, style={}}) => (
  <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:16,
    padding:'18px 20px',backdropFilter:'blur(12px)',
    boxShadow:'0 1px 6px rgba(0,0,0,0.05)',...style}}>
    {children}
  </div>
);

/* ── Plan badge ──────────────────────────────────────────────────────────────── */
const PLAN_COLOR = { free:'#6b7280', pro:T.cyan, business:T.amber };
function PlanBadge({ plan }) {
  const c = PLAN_COLOR[plan] || T.muted;
  return (
    <span style={{fontSize:10,fontWeight:700,padding:'3px 9px',borderRadius:99,
      background:`rgba(${rgb(c)},.12)`,color:c,letterSpacing:.8,textTransform:'uppercase',
      fontFamily:'JetBrains Mono,monospace',border:`1px solid rgba(${rgb(c)},.2)`}}>
      {plan||'free'}
    </span>
  );
}

/* ── Alert card ──────────────────────────────────────────────────────────────── */
const ACC_ICONS = { vehicle:'🚗', bag:'🎒', keyring:'🔑', pet:'🐾', home:'🏠', card:'💳', other:'📦' };
function AlertCard({ alert, isNew }) {
  const { accessoryFlapid, accessoryType, tagMeta, finder } = alert;
  return (
    <div style={{background:isNew?'rgba(251,191,36,0.04)':T.surface,
      border:`1px solid ${isNew?T.amber:T.border}`,borderRadius:14,padding:'14px 16px',
      position:'relative',boxShadow:'0 1px 4px rgba(0,0,0,0.04)'}}>
      {isNew&&<span style={{position:'absolute',top:10,right:12,fontSize:9,fontWeight:800,
        padding:'2px 8px',borderRadius:99,background:'rgba(251,191,36,.12)',color:T.amber,
        border:`1px solid rgba(251,191,36,.25)`,fontFamily:'JetBrains Mono,monospace',
        letterSpacing:.8,textTransform:'uppercase'}}>NEW</span>}
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
        <div style={{width:36,height:36,borderRadius:10,background:`rgba(${rgb(T.cyan)},.08)`,
          border:`1px solid ${T.dim}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>
          {ACC_ICONS[accessoryType]||'📦'}
        </div>
        <div style={{minWidth:0,flex:1}}>
          <div style={{fontSize:13,fontWeight:700,color:T.text,fontFamily:'JetBrains Mono,monospace'}}>
            {tagMeta?.plate?<span style={{letterSpacing:2,color:T.cyan}}>{tagMeta.plate}</span>
              :tagMeta?.petName||tagMeta?.desc||accessoryFlapid}
          </div>
          <div style={{fontSize:10,color:T.muted,marginTop:1,textTransform:'uppercase',letterSpacing:.8}}>
            {accessoryType} · /{accessoryFlapid}
          </div>
        </div>
        <span style={{fontSize:11,color:T.muted,fontFamily:'JetBrains Mono,monospace',flexShrink:0}}>
          {timeAgo(finder.receivedAt)}
        </span>
      </div>
      <div style={{background:'rgba(0,0,0,0.03)',borderRadius:10,padding:'10px 12px',
        border:`1px solid ${T.dim}`}}>
        <div style={{display:'flex',alignItems:'flex-start',gap:8}}>
          <div style={{width:28,height:28,borderRadius:7,flexShrink:0,
            background:`linear-gradient(135deg,${T.cyan},${T.teal})`,
            display:'flex',alignItems:'center',justifyContent:'center',
            fontSize:11,fontWeight:800,color:'#fff'}}>
            {(finder.name||'?')[0].toUpperCase()}
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap',marginBottom:4}}>
              <span style={{fontSize:13,fontWeight:700,color:T.text}}>{finder.name}</span>
              {finder.phone&&<a href={`tel:${finder.phone}`}
                style={{fontSize:11,color:T.cyan,fontFamily:'JetBrains Mono,monospace',
                  textDecoration:'none',background:`rgba(${rgb(T.cyan)},.08)`,
                  padding:'2px 7px',borderRadius:99,border:`1px solid ${T.dim}`}}>📞 {finder.phone}</a>}
              {finder.phone&&<a href={`https://wa.me/${finder.phone.replace(/\D/g,'')}`}
                target="_blank" rel="noreferrer"
                style={{fontSize:11,color:'#25D366',textDecoration:'none',
                  background:'rgba(37,211,102,.07)',padding:'2px 7px',borderRadius:99,
                  border:'1px solid rgba(37,211,102,.2)'}}>💬 WA</a>}
            </div>
            {finder.reason&&<div style={{fontSize:12,color:T.amber,marginBottom:3,
              display:'flex',alignItems:'center',gap:5}}>
              <span>⚡</span><span>{finder.reason}</span></div>}
            {finder.message&&<div style={{fontSize:12,color:T.muted,lineHeight:1.6,
              fontStyle:'italic',borderLeft:`2px solid ${T.dim}`,paddingLeft:8}}>
              "{finder.message}"</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Vehicle tap row ─────────────────────────────────────────────────────────── */
function VehicleTapRow({ tap, accessory, finderContacts=[] }) {
  const [exp, setExp] = useState(false);
  const plate = accessory?.tagMeta?.plate || tap.accessoryFlapid;
  const relevant = finderContacts.filter(c=>c.accessoryFlapid===tap.accessoryFlapid);
  return (
    <div style={{borderBottom:`1px solid ${T.dim}`,paddingBottom:10,marginBottom:10}}>
      <div style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}} onClick={()=>setExp(e=>!e)}>
        <div style={{width:34,height:34,borderRadius:9,background:`rgba(${rgb(T.cyan)},.08)`,
          border:`1px solid ${T.dim}`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>🚗</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <span style={{fontFamily:'JetBrains Mono,monospace',fontWeight:800,fontSize:14,
              color:T.cyan,letterSpacing:1}}>{plate}</span>
            {relevant.length>0&&<span style={{fontSize:10,fontWeight:700,padding:'1px 7px',borderRadius:99,
              background:`rgba(${rgb(T.amber)},.12)`,color:T.amber,border:`1px solid rgba(${rgb(T.amber)},.2)`,
              fontFamily:'JetBrains Mono,monospace'}}>{relevant.length} contact{relevant.length!==1?'s':''}</span>}
          </div>
          <div style={{fontSize:11,color:T.muted,marginTop:2,fontFamily:'JetBrains Mono,monospace'}}>
            /{tap.accessoryFlapid} · {tap.count} tap{tap.count!==1?'s':''}
          </div>
        </div>
        <div style={{textAlign:'right',flexShrink:0}}>
          <div style={{fontSize:20,fontWeight:800,color:T.teal,fontFamily:'Syne,sans-serif'}}>{tap.count}</div>
          <div style={{fontSize:10,color:T.muted}}>{timeAgo(tap.lastTapped)}</div>
        </div>
        <span style={{fontSize:12,color:T.muted,transform:exp?'rotate(180deg)':'none',transition:'transform .2s'}}>▾</span>
      </div>
      {exp&&(
        <div style={{marginTop:10,paddingLeft:44}}>
          {tap.deviceTypes?.length>0&&(
            <div style={{fontSize:11,color:T.muted,marginBottom:8,fontFamily:'JetBrains Mono,monospace'}}>
              Scanned from: {tap.deviceTypes.join(', ')}
            </div>
          )}
          {relevant.length===0&&<div style={{fontSize:12,color:T.muted}}>No finder contacts yet for this vehicle.</div>}
          {relevant.map((c,i)=><AlertCard key={i} alert={c} isNew={new Date(c.finder.receivedAt)>Date.now()-86400000}/>)}
        </div>
      )}
    </div>
  );
}

/* ── Nav item ────────────────────────────────────────────────────────────────── */
function NavItem({ icon, label, active, onClick, badge }) {
  return (
    <button onClick={onClick}
      style={{display:'flex',alignItems:'center',gap:9,padding:'10px 14px',borderRadius:11,
        width:'100%',border:'none',cursor:'pointer',fontFamily:'inherit',
        background:active?`rgba(${rgb(T.cyan)},.08)`:'transparent',
        color:active?T.cyan:T.muted,transition:'all .15s',
        outline:active?`1px solid rgba(${rgb(T.cyan)},.2)`:'none'}}>
      <span style={{fontSize:16,position:'relative'}}>
        {icon}
        {badge>0&&<span style={{position:'absolute',top:-4,right:-6,
          background:T.red,color:'#fff',fontSize:8,fontWeight:800,borderRadius:99,
          minWidth:14,height:14,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 3px'}}>
          {badge>9?'9+':badge}</span>}
      </span>
      <span style={{fontSize:13,fontWeight:active?700:500,fontFamily:'DM Sans,sans-serif'}}>{label}</span>
    </button>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   MAIN DASHBOARD
   ════════════════════════════════════════════════════════════════════════════ */
export default function UserDashboard() {
  const navigate      = useNavigate();
  const bp            = useBreakpoint();
  const [tab, setTab] = useState('overview');
  const [user, setUser]         = useState(null);
  const [stats, setStats]       = useState(null);
  const [alerts, setAlerts]     = useState([]);
  const [alertCount, setAlertCount] = useState(0);
  const [loading, setLoading]   = useState(true);
  const [period, setPeriod]     = useState(30);
  const [unread, setUnread]     = useState(0);
  const tokenPayload            = decodeToken();

  const isAdmin   = ['admin','superadmin'].includes(tokenPayload.role);
  const isMobile  = bp === 'mobile';
  const isDesktop = bp === 'desktop';

  // Profile
  useEffect(() => {
    api.get('/users/profile').then(r=>setUser(r.data)).catch(()=>navigate('/login'));
  }, [navigate]);

  // Analytics
  const loadStats = useCallback(async () => {
    setLoading(true);
    try { const {data}=await api.get(`/analytics/my?days=${period}`); setStats(data); }
    catch { setStats(null); }
    setLoading(false);
  }, [period]);
  useEffect(() => { loadStats(); }, [loadStats]);

  // Alerts
  const loadAlerts = useCallback(async () => {
    try { const {data}=await api.get('/flapfindmy/my-alerts'); setAlerts(data.alerts||[]); setAlertCount(data.total||0); }
    catch { setAlerts([]); }
  }, []);
  useEffect(() => { loadAlerts(); const i=setInterval(loadAlerts,30000); return()=>clearInterval(i); }, [loadAlerts]);

  // Unread
  useEffect(() => { api.get('/messages/unread-count').then(r=>setUnread(r.data.unread||0)).catch(()=>{}); }, []);

  const logout = () => { localStorage.removeItem('token'); navigate('/login'); };

  const s   = stats?.summary || {};
  const dt  = stats?.dailyTrend || [];
  const bT  = stats?.byType || [];
  const dev = stats?.devices || [];
  const at  = stats?.accessoryTaps || [];  // accessory taps from analytics

  // Vehicle-tagged accessories from user profile
  const vehicleAccs = (user?.accessories||[]).filter(a=>a.type==='vehicle'||a.tagMeta?.plate);
  // Vehicle analytics taps (cross-reference flapid with accessories)
  const vehicleAccFlapids = new Set(vehicleAccs.map(a=>a.flapid||a.uid));
  const vehicleTaps = at.filter(t=>vehicleAccFlapids.has(t.accessoryFlapid)||vehicleAccs.length===0&&t.accessoryFlapid);

  const devTotal = dev.reduce((a,d)=>a+d.count,0)||1;
  const devSegs  = dev.map(d=>({value:d.count,color:{mobile:T.cyan,desktop:T.purple,tablet:T.teal,unknown:T.muted}[d.device]||T.muted,label:d.device}));
  const evSegs   = [{value:s.totalViews||0,color:T.cyan,label:'Views'},{value:s.totalTaps||0,color:T.teal,label:'Taps'},{value:s.totalClicks||0,color:T.purple,label:'Clicks'},{value:s.totalSaves||0,color:T.amber,label:'Saves'},{value:s.totalExchanges||0,color:T.green,label:'Exchanges'}];

  const newAlerts = alerts.filter(a=>new Date(a.finder?.receivedAt)>Date.now()-86400000).length;
  const dayAgo    = Date.now()-86400000;

  /* ── NAV ──────────────────────────────────────────────────────────────────── */
  const NAV = [
    {key:'overview', icon:'◈',  label:'Overview'},
    {key:'vehicle',  icon:'🚗', label:'Vehicle Tags', badge:vehicleTaps.length},
    {key:'analytics',icon:'∿',  label:'Analytics'},
    {key:'alerts',   icon:'🔔', label:'Tag Alerts',   badge:newAlerts},
    {key:'activity', icon:'⬡',  label:'Activity'},
    {key:'messages', icon:'💬', label:'Messages',     action:()=>navigate('/messages'), badge:unread},
    {key:'profile',  icon:'○',  label:'Profile',      action:()=>navigate('/profile')},
  ];
  if (isAdmin) NAV.push({key:'admin',icon:'⬟',label:'Admin',action:()=>navigate('/admin')});

  /* ── SIDEBAR ──────────────────────────────────────────────────────────────── */
  const Sidebar = () => (
    <div style={{
      width:isDesktop?220:'100%',
      background:isDesktop?'rgba(255,255,255,0.97)':'rgba(255,255,255,0.98)',
      borderRight:isDesktop?`1px solid ${T.border}`:'none',
      borderTop:!isDesktop?`1px solid ${T.border}`:'none',
      display:'flex',flexDirection:isDesktop?'column':'row',
      padding:isDesktop?'16px 10px':'6px 8px',
      gap:isDesktop?2:0,
      justifyContent:!isDesktop?'space-around':'flex-start',
      flexShrink:0,zIndex:50,backdropFilter:'blur(20px)',
      boxShadow:isDesktop?'1px 0 0 rgba(0,0,0,0.06)':'0 -1px 0 rgba(0,0,0,0.06)',
    }}>
      {isDesktop&&(
        <div style={{padding:'12px 14px 18px',marginBottom:8,
          borderBottom:`1px solid ${T.dim}`}}>
          <div style={{fontFamily:'Syne,sans-serif',fontWeight:900,fontSize:22,
            color:T.cyan,letterSpacing:-.5}}>flap</div>
          <div style={{fontSize:10,color:T.muted,letterSpacing:2,
            fontFamily:'JetBrains Mono,monospace'}}>DASHBOARD</div>
        </div>
      )}
      {NAV.map(n=>(
        isDesktop?(
          <NavItem key={n.key} icon={n.icon} label={n.label}
            active={tab===n.key&&!n.action} badge={n.badge}
            onClick={()=>n.action?n.action():setTab(n.key)}/>
        ):(
          <button key={n.key} onClick={()=>n.action?n.action():setTab(n.key)}
            style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',
              gap:3,padding:'5px 4px',border:'none',cursor:'pointer',
              background:'transparent',position:'relative'}}>
            <span style={{fontSize:17,filter:tab===n.key&&!n.action?`drop-shadow(0 0 4px ${T.cyan})`:'none'}}>
              {n.icon}
            </span>
            <span style={{fontSize:8,color:tab===n.key&&!n.action?T.cyan:T.muted,
              fontFamily:'JetBrains Mono,monospace',textTransform:'uppercase'}}>
              {n.label.split(' ')[0]}
            </span>
            {(n.badge||0)>0&&<span style={{position:'absolute',top:2,right:'15%',
              background:T.red,color:'#fff',fontSize:8,fontWeight:800,borderRadius:99,
              minWidth:14,height:14,display:'flex',alignItems:'center',justifyContent:'center'}}>
              {n.badge>9?'9+':n.badge}</span>}
          </button>
        )
      ))}
      {isDesktop&&user&&(
        <div style={{marginTop:'auto',paddingTop:12,borderTop:`1px solid ${T.dim}`}}>
          <div style={{display:'flex',alignItems:'center',gap:9,padding:'10px 12px',
            background:`rgba(${rgb(T.cyan)},.05)`,borderRadius:11,marginBottom:8}}>
            <div style={{width:34,height:34,borderRadius:10,flexShrink:0,overflow:'hidden',
              border:`2px solid rgba(${rgb(T.cyan)},.3)`,
              background:`linear-gradient(135deg,${T.cyan},${T.teal})`,
              display:'flex',alignItems:'center',justifyContent:'center',
              fontSize:14,fontWeight:800,color:'#fff'}}>
              {user.user_photo
                ?<img src={user.user_photo} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                :(user.username||'?')[0].toUpperCase()}
            </div>
            <div style={{minWidth:0}}>
              <div style={{fontSize:12,fontWeight:700,color:T.text,overflow:'hidden',
                textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{user.username||user.email}</div>
              <PlanBadge plan={user.plan}/>
            </div>
          </div>
          <button onClick={logout}
            style={{width:'100%',padding:'9px 14px',borderRadius:10,cursor:'pointer',
              border:'1px solid rgba(248,113,113,0.2)',background:'rgba(248,113,113,0.05)',
              color:T.red,fontSize:12,fontWeight:600,display:'flex',alignItems:'center',
              gap:7,fontFamily:'DM Sans,sans-serif'}}>
            ⏻ Logout
          </button>
        </div>
      )}
    </div>
  );

  /* ── PROFILE CARD (replaces 3D avatar — clean & consistent with profile page) */
  const ProfileCard = () => {
    if (!user) return null;
    return (
      <Card style={{display:'flex',flexDirection:'column',alignItems:'center',
        textAlign:'center',padding:'28px 20px',gap:4,
        background:`linear-gradient(160deg,rgba(56,189,248,.06) 0%,rgba(255,255,255,1) 60%)`,
        position:'relative',overflow:'hidden'}}>
        {/* Background glow */}
        <div style={{position:'absolute',top:-30,right:-30,width:120,height:120,
          background:`radial-gradient(circle,rgba(${rgb(T.cyan)},.12),transparent 70%)`,
          pointerEvents:'none'}}/>
        {/* Avatar */}
        <div style={{width:80,height:80,borderRadius:'50%',overflow:'hidden',
          border:`3px solid ${user.user_colour||T.cyan}`,
          boxShadow:`0 0 0 2px rgba(${rgb(user.user_colour||T.cyan)},.2),0 8px 24px rgba(${rgb(user.user_colour||T.cyan)},.2)`,
          background:`rgba(${rgb(user.user_colour||T.cyan)},.1)`,
          display:'flex',alignItems:'center',justifyContent:'center',
          fontSize:32,fontWeight:900,fontFamily:'Syne,sans-serif',color:user.user_colour||T.cyan,
          marginBottom:8,flexShrink:0}}>
          {user.user_photo
            ?<img src={user.user_photo} alt={user.username} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
            :(user.username||'?')[0].toUpperCase()}
        </div>
        {/* Name */}
        <div style={{fontFamily:'Syne,sans-serif',fontWeight:900,fontSize:20,
          color:T.text,letterSpacing:-.3}}>{user.username||user.email}</div>
        {/* Role / org */}
        {user.jobtitle&&<div style={{fontSize:13,color:user.user_colour||T.cyan,fontWeight:700,marginTop:2}}>
          {user.jobtitle}</div>}
        {user.organization&&<div style={{fontSize:12,color:T.muted,marginTop:1}}>{user.organization}</div>}
        {/* Plan badge */}
        <div style={{marginTop:6}}><PlanBadge plan={user.plan}/></div>
        {/* Quick stats */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:1,
          marginTop:16,width:'100%',borderTop:`1px solid ${T.dim}`,paddingTop:14}}>
          {[
            {label:'Views',    value:s.totalViews||0,  color:T.cyan  },
            {label:'NFC Taps', value:s.totalTaps||0,   color:T.teal  },
            {label:'Saves',    value:s.totalSaves||0,  color:T.amber },
          ].map(m=>(
            <div key={m.label} style={{textAlign:'center'}}>
              <div style={{fontSize:18,fontWeight:800,color:m.color,fontFamily:'Syne,sans-serif'}}>
                <AnimNum value={m.value}/>
              </div>
              <div style={{fontSize:10,color:T.muted,fontFamily:'JetBrains Mono,monospace',
                textTransform:'uppercase',letterSpacing:.8,marginTop:1}}>{m.label}</div>
            </div>
          ))}
        </div>
        {/* Public page link */}
        {user.primaryFlapid&&(
          <a href={'/'+user.primaryFlapid} target="_blank" rel="noreferrer"
            style={{marginTop:12,fontSize:12,color:T.cyan,textDecoration:'none',
              fontWeight:600,display:'flex',alignItems:'center',gap:4}}>
            🌐 View public profile ↗
          </a>
        )}
      </Card>
    );
  };

  /* ── OVERVIEW ─────────────────────────────────────────────────────────────── */
  const OverviewContent = () => (
    <div style={{display:'grid',gap:14,
      gridTemplateColumns:isDesktop?'300px 1fr':'1fr'}}>
      {/* Profile card — spans 2 rows on desktop */}
      <div style={{gridRow:isDesktop?'1/3':'auto'}}><ProfileCard/></div>

      {/* Stat grid */}
      <div style={{display:'grid',gap:10,
        gridTemplateColumns:`repeat(${isMobile?2:3},1fr)`}}>
        <StatCard icon="👁"  label="Views"       value={s.totalViews||0}     color={T.cyan}   delay={80}/>
        <StatCard icon="📲"  label="NFC Taps"    value={s.totalTaps||0}      color={T.teal}   delay={120}/>
        <StatCard icon="🔗"  label="Clicks"      value={s.totalClicks||0}    color={T.purple} delay={160}/>
        <StatCard icon="📇"  label="Saves"       value={s.totalSaves||0}     color={T.amber}  delay={200}/>
        <StatCard icon="🔄"  label="Exchanges"   value={s.totalExchanges||0} color={T.green}  delay={240}/>
        <StatCard icon="🔔"  label="Tag Alerts"  value={alertCount}          color={T.red}
          anim={false} delay={280} sub={newAlerts>0?`${newAlerts} new today`:'no new alerts'}/>
      </div>

      {/* Engagement rings + device donut */}
      <Card>
        <SecHead title="Engagement" icon="◎"/>
        <div style={{display:'flex',gap:14,flexWrap:'wrap',alignItems:'center'}}>
          {[{l:'Views',v:s.totalViews,m:1000,c:T.cyan},{l:'Taps',v:s.totalTaps,m:500,c:T.teal},
            {l:'Clicks',v:s.totalClicks,m:300,c:T.purple},{l:'Saves',v:s.totalSaves,m:100,c:T.amber}]
            .map(r=>(
              <div key={r.l} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:5}}>
                <Ring pct={(r.v||0)/r.m} color={r.c} size={60}
                  label={`${Math.round(((r.v||0)/r.m)*100)}%`}/>
                <div style={{fontSize:9,color:T.muted,fontFamily:'JetBrains Mono,monospace',
                  textTransform:'uppercase',letterSpacing:.8}}>{r.l}</div>
              </div>
            ))}
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:6}}>
            <Donut segments={devSegs.length?devSegs:[{value:1,color:T.dim}]}
              size={70} thick={10} label={devTotal} sub="scans"/>
            <div style={{display:'flex',gap:6,flexWrap:'wrap',justifyContent:'center'}}>
              {devSegs.map(d=>(
                <div key={d.label} style={{display:'flex',alignItems:'center',gap:3}}>
                  <div style={{width:5,height:5,borderRadius:'50%',background:d.color}}/>
                  <span style={{fontSize:8,color:T.muted,fontFamily:'JetBrains Mono,monospace',
                    textTransform:'uppercase'}}>{d.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Quick actions */}
      <Card>
        <SecHead title="Quick Actions" icon="⚡"/>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <QAction icon="✏️" label="Edit"     color={T.cyan}   onClick={()=>navigate('/profile')}/>
          <QAction icon="📇" label="Contacts" color={T.teal}   onClick={()=>navigate('/contacts')}/>
          <QAction icon="💬" label="Messages" color={T.purple} onClick={()=>navigate('/messages')} badge={unread}/>
          <QAction icon="🔔" label="Alerts"   color={T.amber}  onClick={()=>setTab('alerts')} badge={newAlerts}/>
          <QAction icon="🚗" label="Vehicle"  color={T.green}  onClick={()=>setTab('vehicle')} badge={vehicleTaps.length}/>
          {isAdmin&&<QAction icon="⬟" label="Admin" color={T.red} onClick={()=>navigate('/admin')}/>}
          <QAction icon="⏻" label="Logout"   color={T.muted}  onClick={logout}/>
        </div>
      </Card>
    </div>
  );

  /* ── VEHICLE TAG INTERACTIONS ─────────────────────────────────────────────── */
  const VehicleContent = () => {
    // Finder contacts that are vehicle-related
    const vAlerts = alerts.filter(a=>
      vehicleAccFlapids.has(a.accessoryFlapid)||
      a.accessoryType==='vehicle'||
      a.tagMeta?.plate
    );
    // All vehicle taps (from analytics accessoryTaps)
    const allVTaps = vehicleTaps.length
      ? vehicleTaps
      : vehicleAccs.map(a=>({accessoryFlapid:a.flapid||a.uid,count:0,lastTapped:null,deviceTypes:[]}));

    return (
      <div style={{display:'grid',gap:14}}>
        {/* Header stats */}
        <div style={{display:'grid',gridTemplateColumns:`repeat(${isMobile?2:3},1fr)`,gap:10}}>
          <StatCard icon="🚗" label="Vehicle Tags"    value={vehicleAccs.length}    color={T.cyan}   anim={false} delay={0}/>
          <StatCard icon="📲" label="Vehicle Scans"   value={allVTaps.reduce((a,t)=>a+t.count,0)} color={T.teal} delay={80}/>
          <StatCard icon="📨" label="Finder Contacts" value={vAlerts.length}         color={T.amber}  anim={false} delay={160}/>
        </div>

        {/* No vehicles registered */}
        {vehicleAccs.length===0&&(
          <Card style={{textAlign:'center',padding:'40px 20px'}}>
            <div style={{fontSize:48,marginBottom:14}}>🚗</div>
            <div style={{fontFamily:'Syne,sans-serif',fontSize:16,fontWeight:800,
              color:T.text,marginBottom:8}}>No vehicle tags yet</div>
            <div style={{fontSize:13,color:T.muted,maxWidth:340,margin:'0 auto 20px',lineHeight:1.7}}>
              Add a vehicle tag in your Profile → Tags tab. Once registered, you'll see
              every scan here along with the reason someone contacted you.
            </div>
            <button onClick={()=>navigate('/profile')}
              style={{padding:'10px 24px',borderRadius:10,border:`1px solid ${T.dim}`,
                background:`rgba(${rgb(T.cyan)},.06)`,color:T.cyan,
                cursor:'pointer',fontSize:13,fontWeight:700,fontFamily:'DM Sans,sans-serif'}}>
              Manage Tags →
            </button>
          </Card>
        )}

        {/* Vehicle list with expandable tap history */}
        {allVTaps.length>0&&(
          <Card>
            <SecHead title="Vehicle Scan History" icon="📊"/>
            {allVTaps.map((tap,i)=>(
              <VehicleTapRow
                key={tap.accessoryFlapid||i}
                tap={tap}
                accessory={vehicleAccs.find(a=>(a.flapid||a.uid)===tap.accessoryFlapid)}
                finderContacts={vAlerts}
              />
            ))}
          </Card>
        )}

        {/* Recent finder contacts for vehicles */}
        {vAlerts.length>0&&(
          <Card>
            <SecHead title="Vehicle Finder Contacts" icon="📨"
              action={()=>setTab('alerts')} actionLabel={`View all ${alertCount} →`}/>
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {vAlerts.slice(0,5).map((a,i)=>(
                <AlertCard key={i} alert={a} isNew={new Date(a.finder?.receivedAt)>dayAgo}/>
              ))}
            </div>
          </Card>
        )}
      </div>
    );
  };

  /* ── ANALYTICS ────────────────────────────────────────────────────────────── */
  const AnalyticsContent = () => (
    <div style={{display:'grid',gap:14}}>
      <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
        <span style={{fontSize:11,color:T.muted,fontFamily:'JetBrains Mono,monospace',
          textTransform:'uppercase',letterSpacing:1}}>Period:</span>
        {[7,30,90].map(d=>(
          <button key={d} onClick={()=>setPeriod(d)}
            style={{padding:'6px 14px',borderRadius:99,border:'none',cursor:'pointer',
              background:period===d?T.cyan:`rgba(${rgb(T.cyan)},.08)`,
              color:period===d?'#fff':T.muted,fontWeight:period===d?700:500,
              fontSize:12,fontFamily:'JetBrains Mono,monospace'}}>
            {d}d
          </button>
        ))}
        {loading&&<span style={{fontSize:11,color:T.cyan}}>Loading…</span>}
      </div>

      <div style={{display:'grid',gridTemplateColumns:isDesktop?'1fr 1fr':'1fr',gap:14}}>
        <Card>
          <SecHead title="Daily Views" icon="👁"/>
          <div style={{fontSize:26,fontWeight:800,color:T.cyan,fontFamily:'Syne,sans-serif',marginBottom:10}}>
            <AnimNum value={s.totalViews||0}/>
          </div>
          <LineChart data={dt.map(d=>({value:d.views||0,label:d.date}))} color={T.cyan} height={72}/>
        </Card>
        <Card>
          <SecHead title="NFC Taps" icon="📲"/>
          <div style={{fontSize:26,fontWeight:800,color:T.teal,fontFamily:'Syne,sans-serif',marginBottom:10}}>
            <AnimNum value={s.totalTaps||0}/>
          </div>
          <SparkBars data={dt.map(d=>({value:d.taps||d.views||0}))} color={T.teal} h={60}/>
        </Card>
      </div>

      <div style={{display:'grid',gridTemplateColumns:isDesktop?'1fr 260px':'1fr',gap:14}}>
        <Card>
          <SecHead title="Events breakdown" icon="∿"/>
          {bT.length===0&&<div style={{color:T.muted,fontSize:13}}>No events yet</div>}
          {bT.map((e,i)=>{
            const max=Math.max(...bT.map(x=>x.total),1);
            return (
              <div key={i} style={{marginBottom:12}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:5,fontSize:12}}>
                  <span style={{color:T.text}}>{e.eventType.replace(/_/g,' ')}</span>
                  <span style={{color:T.cyan,fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>{e.total}</span>
                </div>
                <div style={{height:5,borderRadius:3,background:T.dim}}>
                  <div style={{height:'100%',borderRadius:3,
                    background:`linear-gradient(90deg,${T.cyan},${T.teal})`,
                    width:`${(e.total/max)*100}%`,transition:'width 1s ease'}}/>
                </div>
              </div>
            );
          })}
        </Card>
        <Card style={{display:'flex',flexDirection:'column',alignItems:'center',gap:14}}>
          <SecHead title="Mix" icon="◎"/>
          <Donut segments={evSegs.filter(e=>e.value>0).length?evSegs:[{value:1,color:T.dim}]}
            size={130} thick={16} label={s.totalViews||'—'} sub="views"/>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,width:'100%'}}>
            {evSegs.map(s=>(
              <div key={s.label} style={{display:'flex',alignItems:'center',gap:5}}>
                <div style={{width:7,height:7,borderRadius:2,background:s.color,flexShrink:0}}/>
                <div>
                  <div style={{fontSize:9,color:T.muted}}>{s.label}</div>
                  <div style={{fontSize:11,fontWeight:700,color:s.color,fontFamily:'JetBrains Mono,monospace'}}>{s.value}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );

  /* ── LOST TAG ALERTS ──────────────────────────────────────────────────────── */
  const AlertsContent = () => (
    <div style={{display:'grid',gap:14}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8}}>
        <div>
          <h2 style={{fontFamily:'Syne,sans-serif',fontSize:18,fontWeight:900,color:T.text,margin:0}}>Lost Tag Alerts</h2>
          <div style={{fontSize:12,color:T.muted,marginTop:3}}>Finder contacts from all your lost NFC tags</div>
        </div>
        <button onClick={loadAlerts}
          style={{padding:'8px 16px',borderRadius:10,border:`1px solid ${T.dim}`,
            background:`rgba(${rgb(T.cyan)},.06)`,color:T.cyan,cursor:'pointer',
            fontSize:12,fontWeight:700,fontFamily:'DM Sans,sans-serif'}}>↻ Refresh</button>
      </div>
      <div style={{display:'grid',gridTemplateColumns:`repeat(${isMobile?2:3},1fr)`,gap:10}}>
        <StatCard icon="🔔" label="Total Alerts" value={alertCount} color={T.amber} anim={false} delay={0}/>
        <StatCard icon="⭐" label="New Today"    value={newAlerts}  color={T.orange} anim={false} delay={60}/>
        <StatCard icon="🏷️" label="Tags w/ Alerts"
          value={[...new Set(alerts.map(a=>a.accessoryFlapid))].length}
          color={T.red} anim={false} delay={120}/>
      </div>
      {alerts.length===0?(
        <Card style={{textAlign:'center',padding:'40px 20px'}}>
          <div style={{fontSize:48,marginBottom:14}}>🏷️</div>
          <div style={{fontFamily:'Syne,sans-serif',fontSize:16,fontWeight:800,color:T.text,marginBottom:8}}>
            No alerts yet
          </div>
          <div style={{fontSize:13,color:T.muted,maxWidth:360,margin:'0 auto 20px',lineHeight:1.7}}>
            When someone taps your lost tag and leaves their contact, it appears here automatically.
          </div>
          <button onClick={()=>navigate('/profile')}
            style={{padding:'10px 24px',borderRadius:10,border:`1px solid ${T.dim}`,
              background:`rgba(${rgb(T.cyan)},.06)`,color:T.cyan,cursor:'pointer',
              fontSize:13,fontWeight:700,fontFamily:'DM Sans,sans-serif'}}>
            Manage Tags →
          </button>
        </Card>
      ):(
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {alerts.map((a,i)=>(
            <AlertCard key={i} alert={a} isNew={new Date(a.finder?.receivedAt)>dayAgo}/>
          ))}
        </div>
      )}
    </div>
  );

  /* ── ACTIVITY ─────────────────────────────────────────────────────────────── */
  const ActivityContent = () => user&&(
    <div style={{display:'grid',gap:14}}>
      <Card>
        <SecHead title="Account Info" icon="○"/>
        <div style={{display:'grid',gridTemplateColumns:isDesktop?'1fr 1fr 1fr':'1fr 1fr',gap:10}}>
          {[['Username',user.username||'—',T.cyan],['Flap ID',user.primaryFlapid||'—',T.teal],
            ['Plan',user.plan||'free',T.amber],['Role',user.role||'user',T.purple],
            ['Status',user.status||'active',T.green],['Tags',user.accessories?.length||0,T.red]]
            .map(([l,v,c])=>(
              <div key={l} style={{padding:'12px 14px',borderRadius:11,
                background:`rgba(${rgb(c)},.04)`,border:`1px solid rgba(${rgb(c)},.12)`}}>
                <div style={{fontSize:10,color:T.muted,textTransform:'uppercase',
                  letterSpacing:1,marginBottom:4,fontFamily:'JetBrains Mono,monospace'}}>{l}</div>
                <div style={{fontSize:14,fontWeight:700,color:c,fontFamily:'JetBrains Mono,monospace',
                  overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{String(v)}</div>
              </div>
            ))}
        </div>
        {user.accessories?.length>0&&(
          <div style={{marginTop:14}}>
            <div style={{fontSize:11,color:T.muted,textTransform:'uppercase',letterSpacing:1,
              marginBottom:8,fontFamily:'JetBrains Mono,monospace'}}>My Tags</div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {user.accessories.map((acc,i)=>(
                <div key={i} style={{padding:'8px 12px',borderRadius:10,
                  background:acc.isPrimary?`rgba(${rgb(T.cyan)},.08)`:`rgba(0,0,0,.03)`,
                  border:`1px solid ${acc.isLost?T.red:acc.isPrimary?`rgba(${rgb(T.cyan)},.3)`:T.dim}`}}>
                  <div style={{fontSize:12,fontWeight:700,color:T.text,fontFamily:'JetBrains Mono,monospace'}}>
                    {ACC_ICONS[acc.type]||'📦'} {acc.flapid||acc.uid||'—'}
                  </div>
                  <div style={{fontSize:10,color:T.muted,marginTop:2}}>
                    {acc.type}{acc.isPrimary?' · primary':''}{acc.isLost?' · 🔴 lost':''}
                    {acc.tagMeta?.plate&&` · ${acc.tagMeta.plate}`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );

  const TAB_CONTENT = {
    overview: <OverviewContent/>,
    vehicle:  <VehicleContent/>,
    analytics:<AnalyticsContent/>,
    alerts:   <AlertsContent/>,
    activity: <ActivityContent/>,
  };

  const TAB_TITLES = {
    overview:'Overview',vehicle:'Vehicle Tags',analytics:'Analytics',
    alerts:'Lost Tag Alerts',activity:'Activity',
  };

  /* ── LAYOUT ───────────────────────────────────────────────────────────────── */
  return (
    <div style={{height:'100vh',display:'flex',flexDirection:isDesktop?'row':'column',
      background:T.bg,color:T.text,fontFamily:'DM Sans,sans-serif',overflow:'hidden'}}>
      <style>{`
        ${FONTS}
        *{box-sizing:border-box;}
        ::-webkit-scrollbar{width:4px;}
        ::-webkit-scrollbar-track{background:transparent;}
        ::-webkit-scrollbar-thumb{background:rgba(56,189,248,0.2);border-radius:99px;}
      `}</style>

      {/* Subtle background grid */}
      <div style={{position:'fixed',inset:0,pointerEvents:'none',zIndex:0,
        backgroundImage:`linear-gradient(rgba(56,189,248,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(56,189,248,0.03) 1px,transparent 1px)`,
        backgroundSize:'48px 48px'}}/>

      {isDesktop&&<Sidebar/>}

      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',position:'relative',zIndex:1,minHeight:0}}>
        {/* Shared Navbar — shows branding, back to profile, logout */}
        <Navbar/>

        {/* Content */}
        <div style={{flex:1,overflowY:'auto',padding:isMobile?'14px':'22px 28px'}}>
          {TAB_CONTENT[tab]||<OverviewContent/>}
        </div>
      </div>

      {!isDesktop&&<Sidebar/>}
    </div>
  );
}